import React from 'react';
import { useIntl } from 'react-intl';
import clsx from 'clsx';
import { TypedMemo } from '../../common/types';
import { COVER_TOOLS, useCoverEditor, type CoverTool } from '../CoverEditorContext';
import { coverEditorMessages } from '../coverEditorMessages';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the cover editor; each part uses its own slice of it */
import styles from '../CoverEditor.module.scss';
/* eslint-enable css-modules/no-unused-class */

const TOOL_MESSAGES = {
  picture: coverEditorMessages.toolPicture,
  text: coverEditorMessages.toolText,
  effects: coverEditorMessages.toolEffects,
  layers: coverEditorMessages.toolLayers,
} as const;

/** The line drawings of the four tools (design cover-editor b1). */
const TOOL_ICONS: Readonly<Record<CoverTool, React.ReactNode>> = {
  picture: (
    <>
      <rect x="3.5" y="3.5" width="17" height="17" rx="3" />
      <circle cx="9" cy="9" r="1.8" />
      <path d="M4 17l5-5 4 4 3-3 4 4" />
    </>
  ),
  text: (
    <>
      <path d="M5 6h14" />
      <path d="M12 6v13" />
      <path d="M9.5 19h5" />
    </>
  ),
  effects: (
    <>
      <circle cx="12" cy="12" r="3.2" />
      <path d="M12 3v3" />
      <path d="M12 18v3" />
      <path d="M3 12h3" />
      <path d="M18 12h3" />
    </>
  ),
  layers: (
    <>
      <path d="M12 4l8 4-8 4-8-4z" />
      <path d="M4 12l8 4 8-4" />
      <path d="M4 16l8 4 8-4" />
    </>
  ),
};

interface ToolRailProps {
  /** On a phone a second tap on the open tool closes its sheet. */
  isPanelOpen: boolean;
  onPick: (tool: CoverTool) => void;
}

/** The four tools of the editor: the left rail on a wide screen, the bottom bar on a phone (design b1, f1). */
const ToolRailComponent: React.FC<ToolRailProps> = ({ isPanelOpen, onPick }) => {
  const intl = useIntl();
  const { tool } = useCoverEditor();
  return (
    <nav className={styles.rail} aria-label={intl.formatMessage(coverEditorMessages.toolsLabel)}>
      {COVER_TOOLS.map((item) => (
        <button
          key={item}
          type="button"
          className={clsx(styles.railTool, item === tool && isPanelOpen && styles.railToolOn)}
          aria-pressed={item === tool && isPanelOpen}
          onClick={() => onPick(item)}
        >
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.8"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden
          >
            {TOOL_ICONS[item]}
          </svg>
          {intl.formatMessage(TOOL_MESSAGES[item])}
        </button>
      ))}
    </nav>
  );
};

export const ToolRail = TypedMemo(ToolRailComponent);
